// archivio.ts — dove il registratore tiene i fotogrammi durante la partita, con la memoria sotto controllo.
//
// Durante il round non si registra un video continuo (un WebM di MediaRecorder spesso non si può saltare e una
// partita di 10 minuti peserebbe troppo): si tengono fotogrammi singoli già compressi (JPEG), ognuno col suo tempo.
// Così il montaggio può prendere qualsiasi istante, anche al rallentatore, senza decodificare un video.
//
// Tre «cassetti» con regole diverse:
// - conto:  i fotogrammi del conto alla rovescia (tolti tutti tranne gli ultimi secondi quando arriva il VIA);
// - denso:  gli ultimi N fotogrammi, tutti (servono i secondi normali e il rallentatore). Anello di dimensione fissa;
// - rado:   un fotogramma ogni `passoRado` ms per tutta la partita (serve il tratto accelerato). Quando sono troppi
//           se ne toglie uno sì e uno no e il passo raddoppia: la memoria resta limitata anche dopo 10 minuti.
// L'audio segue lo stesso schema (conto + anello degli ultimi secondi), in PCM a 16 bit mono.

export interface Fotogramma {
  t: number; // tempo di cattura (orologio `adesso`)
  blob: Blob; // JPEG
}

export interface LimitiArchivio {
  fotogrammiDensi: number;
  massimoRadi: number;
  passoRadoIniziale: number;
  contoMassimoMs: number;
  byteMassimi: number;
}

export class ArchivioFotogrammi {
  private conto: Fotogramma[] = [];
  private denso: Fotogramma[] = [];
  private rado: Fotogramma[] = [];
  private ultimoRado = -Infinity;
  private passoRado: number;
  private via: number | null = null;
  private limiti: LimitiArchivio;
  private byteConto = 0;
  private byteDenso = 0;
  private byteRado = 0;
  /** Dopo la risata non si toglie più niente dall'anello denso (servono i secondi prima della risata). */
  private bloccato = false;

  constructor(limiti: LimitiArchivio) {
    this.limiti = limiti;
    this.passoRado = limiti.passoRadoIniziale;
  }

  segnaVia(t: number): void {
    this.via = t;
    // Del conto restano solo gli ultimi secondi prima del VIA.
    const tieni = this.conto.filter((f) => f.t >= t - this.limiti.contoMassimoMs - 200 && f.t <= t);
    const sposta = this.conto.filter((f) => f.t > t);
    this.conto = tieni;
    this.byteConto = tieni.reduce((s, f) => s + f.blob.size, 0);
    for (const f of sposta) this.aggiungiDopoVia(f);
  }

  blocca(): void {
    this.bloccato = true;
  }

  aggiungi(f: Fotogramma): void {
    if (this.via === null || f.t <= this.via) {
      this.conto.push(f);
      this.byteConto += f.blob.size;
      // Un conto alla rovescia lunghissimo (VIA mai segnato) non deve riempire la memoria.
      while (this.conto.length > 2 && this.conto[this.conto.length - 1]!.t - this.conto[0]!.t > this.limiti.contoMassimoMs * 4) {
        this.byteConto -= this.conto.shift()!.blob.size;
      }
      return;
    }
    this.aggiungiDopoVia(f);
  }

  private aggiungiDopoVia(f: Fotogramma): void {
    this.denso.push(f);
    this.byteDenso += f.blob.size;
    if (!this.bloccato)
      while (this.denso.length > this.limiti.fotogrammiDensi) this.byteDenso -= this.denso.shift()!.blob.size;
    if (f.t - this.ultimoRado >= this.passoRado) {
      this.rado.push(f);
      this.byteRado += f.blob.size;
      this.ultimoRado = f.t;
      if (this.rado.length > this.limiti.massimoRadi) this.dimezzaRadi();
    }
    // Memoria: se si supera il tetto si sfoltiscono i radi (il tratto accelerato regge anche pochi fotogrammi).
    while (this.byte() > this.limiti.byteMassimi && this.rado.length > 8) this.dimezzaRadi();
  }

  private dimezzaRadi(): void {
    this.rado = this.rado.filter((_, i) => i % 2 === 0);
    this.byteRado = this.rado.reduce((s, x) => s + x.blob.size, 0);
    this.passoRado *= 2;
  }

  /** Byte occupati, contando una volta sola i fotogrammi che stanno sia nel denso sia nel rado. */
  byte(): number {
    let doppi = 0;
    if (this.rado.length && this.denso.length) {
      const primoDenso = this.denso[0]!.t;
      for (let i = this.rado.length - 1; i >= 0 && this.rado[i]!.t >= primoDenso; i--) doppi += this.rado[i]!.blob.size;
    }
    return this.byteConto + this.byteDenso + this.byteRado - doppi;
  }

  conteggi(): { conto: number; denso: number; rado: number; passoRado: number } {
    return { conto: this.conto.length, denso: this.denso.length, rado: this.rado.length, passoRado: this.passoRado };
  }

  /** Tutti i fotogrammi, ordinati per tempo e senza doppioni (il rado contiene copie del denso). */
  tutti(): Fotogramma[] {
    const visti = new Set<Fotogramma>();
    const out: Fotogramma[] = [];
    for (const f of [...this.conto, ...this.rado, ...this.denso]) {
      if (visti.has(f)) continue;
      visti.add(f);
      out.push(f);
    }
    return out.sort((a, b) => a.t - b.t);
  }

  svuota(): void {
    this.conto = [];
    this.denso = [];
    this.rado = [];
    this.byteConto = this.byteDenso = this.byteRado = 0;
  }
}

/** Indice del fotogramma con tempo ≤ t (o il primo), su un elenco ordinato. */
export function indicePrima(elenco: readonly { t: number }[], t: number): number {
  let lo = 0;
  let hi = elenco.length - 1;
  if (hi < 0) return -1;
  if (t < elenco[0]!.t) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (elenco[mid]!.t <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

interface Blocco {
  t: number; // tempo del primo campione
  campioni: Int16Array;
}

/** Audio mono a 16 bit: il conto alla rovescia più gli ultimi `secondiAnello` secondi (senza le pause). */
export class ArchivioAudio {
  readonly frequenza: number;
  private conto: Blocco[] = [];
  private anello: Blocco[] = [];
  private campioniAnello = 0;
  private via: number | null = null;
  private massimoAnello: number;
  private contoMassimoMs: number;
  private bloccato = false;

  constructor(frequenza: number, secondiAnello: number, contoMassimoMs: number) {
    this.frequenza = frequenza;
    this.massimoAnello = Math.ceil(secondiAnello * frequenza);
    this.contoMassimoMs = contoMassimoMs;
  }

  segnaVia(t: number): void {
    this.via = t;
    this.conto = this.conto.filter((b) => b.t + (b.campioni.length / this.frequenza) * 1000 >= t - this.contoMassimoMs - 200);
  }

  blocca(): void {
    this.bloccato = true;
  }

  aggiungi(t: number, dati: Float32Array): void {
    const c = new Int16Array(dati.length);
    for (let i = 0; i < dati.length; i++) c[i] = Math.max(-32768, Math.min(32767, Math.round((dati[i] ?? 0) * 32767)));
    const b = { t, campioni: c };
    if (this.via === null || t < this.via) {
      this.conto.push(b);
      const durata = this.conto.reduce((s, x) => s + x.campioni.length, 0) / this.frequenza;
      if (durata > (this.contoMassimoMs * 4) / 1000) this.conto.shift();
      return;
    }
    this.anello.push(b);
    this.campioniAnello += c.length;
    if (!this.bloccato)
      while (this.campioniAnello > this.massimoAnello && this.anello.length > 1) this.campioniAnello -= this.anello.shift()!.campioni.length;
  }

  byte(): number {
    return [...this.conto, ...this.anello].reduce((s, b) => s + b.campioni.byteLength, 0);
  }

  /** Campioni tra `da` e `a` (ms sorgente), alla frequenza dell'archivio; dove manca l'audio c'è silenzio. */
  estrai(da: number, a: number): Float32Array {
    const n = Math.max(0, Math.round(((a - da) / 1000) * this.frequenza));
    const out = new Float32Array(n);
    for (const b of [...this.conto, ...this.anello]) {
      const inizio = Math.round(((b.t - da) / 1000) * this.frequenza);
      if (inizio >= n || inizio + b.campioni.length <= 0) continue;
      for (let i = Math.max(0, -inizio); i < b.campioni.length && inizio + i < n; i++) out[inizio + i] = (b.campioni[i] ?? 0) / 32767;
    }
    return out;
  }

  svuota(): void {
    this.conto = [];
    this.anello = [];
    this.campioniAnello = 0;
  }
}
